// Pure schedule helpers for the routine editor and the calendar: a local mirror of the server's
// schedule maths (server/schedule.js: validateSchedule, nextRun, occurrences, describe — same rules,
// same French wording, local time of the browser) and the mapping between the form "builder" and a
// Schedule object. DOM-free, so node:test covers it (test/ui-automations-schedule.test.js checks
// the mirror against the server module).
import { MONTHS_SHORT, DAYS } from '../../i18n/fr.js';

/** The schedule kinds of SPEC §15. */
export const SCHEDULE_KINDS = ['daily', 'weekdays', 'weekly', 'monthly', 'hourly', 'interval'];
export const MIN_INTERVAL_MINUTES = 5;
export const MAX_INTERVAL_MINUTES = 1440;
export const MAX_HOURLY_EVERY = 24;
/** Monday first, as the calendar shows the week. */
export const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];

const TIMED_KINDS = new Set(['daily', 'weekdays', 'weekly', 'monthly']);
const HHMM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;
const DATE_KEY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MAX_SCAN_DAYS = 400;
const MAX_OCCURRENCE_DAYS = 3700;

const pad2 = (n) => String(n).padStart(2, '0');
const isSet = (v) => v !== undefined && v !== null && v !== '';

/** Minutes since midnight for "HH:MM", or null. */
export function minutesOf(hhmm) {
  const m = typeof hhmm === 'string' ? HHMM_RE.exec(hhmm) : null;
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/** True for a valid "HH:MM" (24 h). */
export function isTime(v) {
  return minutesOf(v) !== null;
}

/**
 * Parses 'YYYY-MM-DD' into local calendar parts (m is 0-based), or null when it is not a real date.
 * @param {string} key
 * @returns {{y: number, m: number, d: number}|null}
 */
export function parseDateKey(key) {
  const r = typeof key === 'string' ? DATE_KEY_RE.exec(key) : null;
  if (!r) return null;
  const y = Number(r[1]);
  const m = Number(r[2]) - 1;
  const d = Number(r[3]);
  const probe = new Date(y, m, d, 12);
  if (probe.getFullYear() !== y || probe.getMonth() !== m || probe.getDate() !== d) return null;
  return { y, m, d };
}

/**
 * Local calendar date of an instant as 'YYYY-MM-DD'.
 * @param {number|Date} ms
 * @returns {string}
 */
export function dateKey(ms) {
  const d = ms instanceof Date ? ms : new Date(Number(ms));
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function daysInMonth(y, m) {
  return new Date(y, m + 1, 0, 12).getDate();
}

function asMs(v) {
  if (v instanceof Date) return v.getTime();
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Checks a schedule (same rules and messages as the server).
 * @param {object} s Schedule
 * @returns {string[]} French problems, empty when valid
 */
export function validateSchedule(s) {
  if (!s || typeof s !== 'object' || Array.isArray(s)) return ['Planification manquante.'];
  if (!SCHEDULE_KINDS.includes(s.kind)) {
    return ['Type de planification inconnu (quotidien, jours ouvrés, hebdomadaire, mensuel, toutes les N heures ou toutes les N minutes).'];
  }
  const problems = [];
  if (TIMED_KINDS.has(s.kind) && minutesOf(s.at) === null) problems.push('Heure invalide : format HH:MM attendu (ex. 08:30).');
  if (s.kind === 'weekly') {
    if (!Array.isArray(s.days) || s.days.length === 0) problems.push('Choisissez au moins un jour de la semaine.');
    else if (!s.days.every((d) => Number.isInteger(d) && d >= 0 && d <= 6)) problems.push('Jours invalides : de 0 (dimanche) à 6 (samedi).');
  }
  if (s.kind === 'monthly' && !(Number.isInteger(s.dayOfMonth) && s.dayOfMonth >= 1 && s.dayOfMonth <= 31)) {
    problems.push('Jour du mois invalide : entre 1 et 31.');
  }
  if (s.kind === 'hourly' && !(Number.isInteger(s.every) && s.every >= 1 && s.every <= MAX_HOURLY_EVERY)) {
    problems.push(`Intervalle invalide : entre 1 et ${MAX_HOURLY_EVERY} heures.`);
  }
  if (s.kind === 'interval' && !(Number.isInteger(s.every) && s.every >= MIN_INTERVAL_MINUTES && s.every <= MAX_INTERVAL_MINUTES)) {
    problems.push(`Intervalle invalide : entre ${MIN_INTERVAL_MINUTES} et ${MAX_INTERVAL_MINUTES} minutes.`);
  }
  if (s.kind === 'hourly' || s.kind === 'interval') {
    const from = isSet(s.from) ? minutesOf(s.from) : 0;
    const to = isSet(s.to) ? minutesOf(s.to) : 1439;
    if (from === null) problems.push('Heure de début invalide : format HH:MM attendu.');
    if (to === null) problems.push('Heure de fin invalide : format HH:MM attendu.');
    if (from !== null && to !== null && from > to) problems.push("L'heure de début doit précéder l'heure de fin.");
    if (isSet(s.weekdaysOnly) && typeof s.weekdaysOnly !== 'boolean') problems.push('« En semaine seulement » doit être vrai ou faux.');
  }
  const start = isSet(s.start) ? parseDateKey(s.start) : undefined;
  const end = isSet(s.end) ? parseDateKey(s.end) : undefined;
  if (start === null) problems.push('Date de début invalide : format AAAA-MM-JJ attendu.');
  if (end === null) problems.push('Date de fin invalide : format AAAA-MM-JJ attendu.');
  if (start && end && s.end < s.start) problems.push('La date de fin doit suivre la date de début.');
  return problems;
}

/** Sorted, de-duplicated run times (ms) of a valid schedule on local day y-m-d. */
function dayCandidates(s, y, m, d) {
  const weekday = new Date(y, m, d, 12).getDay();
  const at = (minutes) => new Date(y, m, d, Math.floor(minutes / 60), minutes % 60, 0, 0).getTime();
  let list = [];
  switch (s.kind) {
    case 'daily': list = [at(minutesOf(s.at))]; break;
    case 'weekdays': if (weekday >= 1 && weekday <= 5) list = [at(minutesOf(s.at))]; break;
    case 'weekly': if (s.days.includes(weekday)) list = [at(minutesOf(s.at))]; break;
    case 'monthly': if (d === Math.min(s.dayOfMonth, daysInMonth(y, m))) list = [at(minutesOf(s.at))]; break;
    case 'hourly':
    case 'interval': {
      if (s.weekdaysOnly === true && (weekday === 0 || weekday === 6)) break;
      const step = s.kind === 'hourly' ? s.every * 60 : s.every;
      const first = isSet(s.from) ? minutesOf(s.from) : 0;
      const last = isSet(s.to) ? minutesOf(s.to) : 1439;
      for (let t = first; t <= last; t += step) list.push(at(t));
      break;
    }
    default: break;
  }
  return [...new Set(list)].sort((a, b) => a - b);
}

function bounds(s) {
  const start = isSet(s.start) ? parseDateKey(s.start) : null;
  const end = isSet(s.end) ? parseDateKey(s.end) : null;
  return {
    startMs: start ? new Date(start.y, start.m, start.d).getTime() : -Infinity,
    endMs: end ? new Date(end.y, end.m, end.d + 1).getTime() : Infinity,
  };
}

/**
 * First run strictly after `fromMs` (start counts from 00:00 that day, the whole end day is
 * included). Null when the schedule is invalid or has ended.
 * @param {object} s Schedule
 * @param {number|Date} fromMs
 * @returns {number|null}
 */
export function nextRun(s, fromMs) {
  if (validateSchedule(s).length) return null;
  const from = asMs(fromMs);
  if (from === null) return null;
  const { startMs, endMs } = bounds(s);
  const floor = Math.max(Math.floor(from) + 1, startMs);
  if (floor >= endMs) return null;
  const base = new Date(Math.max(from, startMs));
  const y = base.getFullYear();
  const m = base.getMonth();
  const d0 = base.getDate();
  for (let i = 0; i < MAX_SCAN_DAYS; i += 1) {
    const day = new Date(y, m, d0 + i, 12);
    const dy = day.getFullYear();
    const dm = day.getMonth();
    const dd = day.getDate();
    if (new Date(dy, dm, dd).getTime() >= endMs) return null;
    for (const t of dayCandidates(s, dy, dm, dd)) {
      if (t >= endMs) return null;
      if (t >= floor) return t;
    }
  }
  return null;
}

/**
 * Every run t with fromMs ≤ t < toMs, in order, at most `max`.
 * @param {object} s Schedule
 * @param {number|Date} fromMs inclusive
 * @param {number|Date} toMs exclusive
 * @param {number} [max=500]
 * @returns {number[]}
 */
export function occurrences(s, fromMs, toMs, max = 500) {
  const out = [];
  const from = asMs(fromMs);
  const to = asMs(toMs);
  const limit = Math.max(0, Math.floor(Number(max) || 0));
  if (from === null || to === null || to <= from || limit === 0 || validateSchedule(s).length) return out;
  const { startMs, endMs } = bounds(s);
  const lo = Math.max(Math.ceil(from), startMs);
  const hi = Math.min(to, endMs);
  if (hi <= lo) return out;
  const base = new Date(lo);
  const y = base.getFullYear();
  const m = base.getMonth();
  const d0 = base.getDate();
  for (let i = 0; i < MAX_OCCURRENCE_DAYS; i += 1) {
    const day = new Date(y, m, d0 + i, 12);
    const dy = day.getFullYear();
    const dm = day.getMonth();
    const dd = day.getDate();
    if (new Date(dy, dm, dd).getTime() >= hi) break;
    for (const t of dayCandidates(s, dy, dm, dd)) {
      if (t < lo) continue;
      if (t >= hi) return out;
      out.push(t);
      if (out.length >= limit) return out;
    }
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// French description (same wording as server/schedule.js describe())

function ordinal(n) {
  return n === 1 ? '1er' : String(n);
}

/** "5 oct.", "1er janv. 2027" (the year only when it differs from `now`'s). */
export function shortDateOfKey(key, now = Date.now()) {
  const p = parseDateKey(key);
  if (!p) return String(key ?? '');
  const day = p.d === 1 ? '1er' : String(p.d);
  const withYear = p.y !== new Date(now).getFullYear();
  return `${day} ${MONTHS_SHORT[p.m]}${withYear ? ` ${p.y}` : ''}`;
}

function listFr(items) {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} et ${items[items.length - 1]}`;
}

function describeDays(days) {
  const set = new Set(days);
  if (set.size === 7) return 'tous les jours';
  if (set.size === 5 && [1, 2, 3, 4, 5].every((d) => set.has(d))) return 'tous les jours ouvrés';
  if (set.size === 6) return `tous les jours sauf le ${DAYS[WEEK_ORDER.find((d) => !set.has(d))]}`;
  return listFr(WEEK_ORDER.filter((d) => set.has(d)).map((d) => `le ${DAYS[d]}`));
}

function describeWindow(s) {
  let out = '';
  if (isSet(s.from) && isSet(s.to)) out += ` de ${s.from} à ${s.to}`;
  else if (isSet(s.from)) out += ` à partir de ${s.from}`;
  else if (isSet(s.to)) out += ` jusqu'à ${s.to}`;
  if (s.weekdaysOnly === true) out += ' en semaine';
  return out;
}

function describeEveryMinutes(n) {
  if (n % 60 === 0) return n === 60 ? 'toutes les heures' : `toutes les ${n / 60} h`;
  return `toutes les ${n} min`;
}

/**
 * The rhythm alone, without the start/end dates: "le samedi à 09:00", "toutes les 2 h de 09:00 à 17:00".
 * @param {object} s Schedule
 * @returns {string} '' when invalid
 */
export function describeRhythm(s) {
  const core = { ...(s || {}) };
  delete core.start;
  delete core.end;
  if (validateSchedule(core).length) return '';
  switch (core.kind) {
    case 'daily': return `tous les jours à ${core.at}`;
    case 'weekdays': return `tous les jours ouvrés à ${core.at}`;
    case 'weekly': return `${describeDays(core.days)} à ${core.at}`;
    case 'monthly':
      return `le ${ordinal(core.dayOfMonth)} de chaque mois${core.dayOfMonth > 28 ? " (ou le dernier jour s'il est plus court)" : ''} à ${core.at}`;
    case 'hourly': return (core.every === 1 ? 'toutes les heures' : `toutes les ${core.every} h`) + describeWindow(core);
    default: return describeEveryMinutes(core.every) + describeWindow(core);
  }
}

/**
 * The start/end part: "à partir du 5 oct.", "du 5 oct. au 20 déc.", "jusqu'au 20 déc." ('' if none).
 * @param {object} s Schedule
 * @param {{now?: number}} [opts]
 */
export function describeBounds(s, { now = Date.now() } = {}) {
  if (!s) return '';
  if (isSet(s.start) && isSet(s.end)) return `du ${shortDateOfKey(s.start, now)} au ${shortDateOfKey(s.end, now)}`;
  if (isSet(s.start)) return `à partir du ${shortDateOfKey(s.start, now)}`;
  if (isSet(s.end)) return `jusqu'au ${shortDateOfKey(s.end, now)}`;
  return '';
}

/**
 * Natural French description, identical to the server's describe(): "tous les jours ouvrés à 08:00",
 * "le lundi et le jeudi à 09:30", "toutes les 30 min", "… à partir du 5 oct.". Never throws.
 * @param {object} s Schedule
 * @param {{now?: number}} [opts]
 * @returns {string}
 */
export function describeSchedule(s, { now = Date.now() } = {}) {
  if (validateSchedule(s).length) return 'planification invalide';
  const bound = describeBounds(s, { now });
  return bound ? `${describeRhythm(s)} ${bound}` : describeRhythm(s);
}

// ---------------------------------------------------------------------------------------------
// Form builder <-> Schedule

/**
 * The editable state of the schedule builder. Every field is kept whatever the kind, so switching
 * kinds back and forth never loses what was typed.
 * @typedef {{kind: string, at: string, days: number[], dayOfMonth: number, everyHours: number,
 *   everyMinutes: number, from: string, to: string, weekdaysOnly: boolean, start: string, end: string}} Builder
 */

/**
 * A fresh builder (daily at 09:00).
 * @param {Partial<Builder>} [patch]
 * @returns {Builder}
 */
export function defaultBuilder(patch = {}) {
  return {
    kind: 'daily', at: '09:00', days: [1], dayOfMonth: 1, everyHours: 2, everyMinutes: 30,
    from: '', to: '', weekdaysOnly: false, start: '', end: '', ...patch,
  };
}

function intOr(v, fallback) {
  const n = Number(v);
  return Number.isInteger(n) ? n : fallback;
}

/**
 * Fills a builder from a Schedule (unknown or missing fields keep the base values).
 * @param {object|null} schedule
 * @param {Builder} [base]
 * @returns {Builder}
 */
export function scheduleToBuilder(schedule, base = defaultBuilder()) {
  const b = { ...base, days: [...(base.days || [])] };
  const s = schedule && typeof schedule === 'object' ? schedule : {};
  if (SCHEDULE_KINDS.includes(s.kind)) b.kind = s.kind;
  if (isTime(s.at)) b.at = s.at;
  if (Array.isArray(s.days)) b.days = sortDays(s.days.filter((d) => Number.isInteger(d) && d >= 0 && d <= 6));
  if (Number.isInteger(s.dayOfMonth)) b.dayOfMonth = s.dayOfMonth;
  if (s.kind === 'hourly') b.everyHours = intOr(s.every, b.everyHours);
  if (s.kind === 'interval') b.everyMinutes = intOr(s.every, b.everyMinutes);
  if (s.kind === 'hourly' || s.kind === 'interval') {
    b.from = isSet(s.from) ? String(s.from) : '';
    b.to = isSet(s.to) ? String(s.to) : '';
    b.weekdaysOnly = s.weekdaysOnly === true;
  }
  b.start = isSet(s.start) ? String(s.start) : '';
  b.end = isSet(s.end) ? String(s.end) : '';
  return b;
}

/**
 * The Schedule object sent to the server for a builder (only the fields of its kind; empty optional
 * fields are left out).
 * @param {Builder} b
 * @returns {object}
 */
export function builderToSchedule(b) {
  const s = { kind: b.kind };
  switch (b.kind) {
    case 'daily':
    case 'weekdays':
      s.at = b.at;
      break;
    case 'weekly':
      s.at = b.at;
      s.days = sortDays(b.days || []);
      break;
    case 'monthly':
      s.at = b.at;
      s.dayOfMonth = intOr(b.dayOfMonth, NaN);
      break;
    case 'hourly':
    case 'interval':
      s.every = intOr(b.kind === 'hourly' ? b.everyHours : b.everyMinutes, NaN);
      if (isSet(b.from)) s.from = b.from;
      if (isSet(b.to)) s.to = b.to;
      if (b.weekdaysOnly) s.weekdaysOnly = true;
      break;
    default: break;
  }
  if (isSet(b.start)) s.start = b.start;
  if (isSet(b.end)) s.end = b.end;
  return s;
}

/** Days sorted Monday first and de-duplicated. */
export function sortDays(days) {
  const set = new Set(days);
  return WEEK_ORDER.filter((d) => set.has(d));
}

/** Toggles one weekday in a list (keeps Monday-first order). */
export function toggleDay(days, day) {
  const set = new Set(days || []);
  if (set.has(day)) set.delete(day); else set.add(day);
  return sortDays([...set]);
}

/**
 * The builder for a repeat preset chosen in the calendar's day popover, anchored on the clicked day:
 * weekly on that weekday, monthly on that date, hourly starting at the chosen time. `start` is the day.
 * @param {'daily'|'weekdays'|'weekly'|'monthly'|'hourly'} kind
 * @param {number} dayMs any instant of the clicked day
 * @param {{at?: string, everyHours?: number, to?: string, days?: number[]}} [opts]
 * @returns {Builder}
 */
export function presetForDay(kind, dayMs, opts = {}) {
  const d = new Date(dayMs);
  const at = isTime(opts.at) ? opts.at : '09:00';
  const b = defaultBuilder({ kind, at, days: opts.days?.length ? sortDays(opts.days) : [d.getDay()], dayOfMonth: d.getDate(), start: dateKey(d) });
  if (kind === 'hourly') {
    b.everyHours = intOr(opts.everyHours, 2);
    b.from = at;
    b.to = isTime(opts.to) ? opts.to : '';
  }
  return b;
}

/**
 * Parts of the live preview line of a routine: "Routine · le samedi à 09:00 · à partir du 26 sept. ·
 * premier passage …". Returns null parts when the schedule is invalid (problems listed).
 * @param {object} schedule
 * @param {{now?: number}} [opts]
 * @returns {{ok: boolean, problems: string[], rhythm: string, bounds: string, first: number|null}}
 */
export function routinePreview(schedule, { now = Date.now() } = {}) {
  const problems = validateSchedule(schedule);
  if (problems.length) return { ok: false, problems, rhythm: '', bounds: '', first: null };
  return {
    ok: true, problems: [], rhythm: describeRhythm(schedule), bounds: describeBounds(schedule, { now }),
    first: nextRun(schedule, now),
  };
}
